import { DossierDetailsContent } from "@/app/pages/dossier/dossier-details-panel.tsx";
import { Bericht, useGetDossierBerichtenById } from "@/entities/bericht";
import { RoleGate, UserRole } from "@/features/auth";
import { useDossierId } from "@/features/dossier-details/hooks.ts";
import {
    Button,
    Empty,
    EmptyContent,
    EmptyDescription,
    EmptyHeader,
    EmptyImage,
    EmptyTitle,
    SingleToggleGroup,
    Skeleton,
    ToggleGroupOption,
} from "@/shared/components";
import { ErrorDisplay } from "@/shared/components/error";
import { useState } from "react";
import { BerichtList } from "./bericht.tsx";
import { NieuwGeneriekBerichtDialog } from "./nieuw-generiek-bericht-dialog.tsx";
import { NieuwSmsBerichtDialog } from "./nieuw-sms-bericht-dialog.tsx";

type BerichtType = "Sms" | "Generiek";

const berichtOptions: ToggleGroupOption<BerichtType>[] = [
    { value: "Generiek", label: "Wissel" },
    { value: "Sms", label: "Medewerker" },
];

export function BerichtenDetails() {
    const id = useDossierId();
    const { data: berichten, isPending, error } = useGetDossierBerichtenById(id);
    const [selectedType, setSelectedType] = useState<BerichtType>("Generiek");
    const [nieuwSmsBerichtOpen, setNieuwSmsBerichtOpen] = useState(false);

    if (isPending) {
        return (
            <DossierDetailsContent className="px-4" data-testid="berichten-details-skeleton">
                <div className="mb-4 flex flex-col gap-2">
                    <div className="grid grid-cols-[auto_auto] gap-2">
                        <Skeleton className="h-10 w-2/3" />
                        <Skeleton className="h-10 w-full" />
                    </div>
                </div>
                <div className="flex flex-col gap-4">
                    <h2 className="text-l leading-m text-content-emphasis font-medium">Verstuurde berichten</h2>
                    <ol className="grid gap-4">
                        <Skeleton className="h-14 w-full" />
                        <Skeleton className="h-14 w-full" />
                        <Skeleton className="h-14 w-full" />
                        <Skeleton className="h-14 w-full" />
                    </ol>
                </div>
            </DossierDetailsContent>
        );
    }

    if (error) {
        return (
            <DossierDetailsContent data-testid="berichten-details-error">
                <ErrorDisplay
                    error={error}
                    variant="section"
                    title="Fout bij het laden van berichten"
                    onRetry={() => window.location.reload()}
                />
            </DossierDetailsContent>
        );
    }

    const berichtenOfType = berichten.filter(Bericht.isType(selectedType)) ?? [];
    const aantalGeneriekeBerichten = berichten.filter(Bericht.isType("Generiek"))?.length ?? 0;
    const aantalSmsBerichten = berichten.filter(Bericht.isType("Sms"))?.length ?? 0;
    const hasBerichten = berichtenOfType.length > 0;

    return (
        <DossierDetailsContent className="px-4">
            <div className="mb-4 flex flex-col gap-2">
                <div className="grid grid-cols-[250px_auto] gap-2">
                    <SingleToggleGroup options={berichtOptions} value={selectedType} onValueChange={setSelectedType} />
                    <RoleGate roles={[UserRole.REGIEVOERDER]}>
                        {selectedType === "Sms" && aantalSmsBerichten > 0 && (
                            <>
                                <Button
                                    variant="secondary"
                                    icon="card"
                                    iconStrokeWidth="thick"
                                    className="ml-auto flex"
                                    onClick={() => setNieuwSmsBerichtOpen(true)}
                                >
                                    Nieuw bericht naar medewerker
                                </Button>
                                {nieuwSmsBerichtOpen && (
                                    <NieuwSmsBerichtDialog
                                        dossierId={id}
                                        open={nieuwSmsBerichtOpen}
                                        onOpenChange={setNieuwSmsBerichtOpen}
                                    />
                                )}
                            </>
                        )}
                        {selectedType === "Generiek" && aantalGeneriekeBerichten > 0 && (
                            <NieuwGeneriekBerichtDialog dossierId={id}>
                                <Button variant="secondary" icon="card" iconStrokeWidth="thick" className="ml-auto flex">
                                    Nieuw Wissel bericht
                                </Button>
                            </NieuwGeneriekBerichtDialog>
                        )}
                    </RoleGate>
                </div>
            </div>

            {hasBerichten ? (
                <div className="flex flex-col gap-4">
                    <h2 className="text-l leading-m text-content-emphasis font-medium">Verstuurde berichten</h2>
                    <BerichtList berichten={berichtenOfType} showOnderwerpBadge />
                </div>
            ) : (
                <GeenBerichten dossierId={id} selectedType={selectedType} />
            )}
        </DossierDetailsContent>
    );
}

type GeenBerichtenProps = {
    dossierId: string;
    selectedType: BerichtType;
};

function GeenBerichten({ dossierId, selectedType }: GeenBerichtenProps) {
    const [nieuwSmsBerichtOpen, setNieuwSmsBerichtOpen] = useState(false);
    return (
        <Empty>
            <EmptyImage />
            <EmptyHeader className="gap-4">
                <EmptyTitle>
                    <h2 className="text-content-subtle">Nog geen berichten</h2>
                </EmptyTitle>
                <EmptyDescription>
                    Er zijn nog geen tekstberichten naar {selectedType === "Generiek" ? "Wissel" : "medewerkers"} verstuurd
                </EmptyDescription>
            </EmptyHeader>
            <EmptyContent>
                <RoleGate roles={[UserRole.REGIEVOERDER]}>
                    {selectedType === "Generiek" && (
                        <NieuwGeneriekBerichtDialog dossierId={dossierId}>
                            <Button variant="secondary" icon="card" iconStrokeWidth="thick">
                                Nieuw Wissel bericht
                            </Button>
                        </NieuwGeneriekBerichtDialog>
                    )}
                    {selectedType === "Sms" && (
                        <>
                            <Button variant="secondary" icon="card" iconStrokeWidth="thick" onClick={() => setNieuwSmsBerichtOpen(true)}>
                                Nieuw medewerker bericht
                            </Button>
                            {nieuwSmsBerichtOpen && (
                                <NieuwSmsBerichtDialog
                                    dossierId={dossierId}
                                    open={nieuwSmsBerichtOpen}
                                    onOpenChange={setNieuwSmsBerichtOpen}
                                />
                            )}
                        </>
                    )}
                </RoleGate>
            </EmptyContent>
        </Empty>
    );
}
